import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { enviroment } from "../../../../enviroment";
import { logoDetailKey } from "../../../../data/repositories/logoDetailCache";
import { isLogoUploadPending } from "../../../../utils/functions/productPending";
import { hasGenuineHighRes } from "../functions/keepSharp";
import {
  isSharpWaitActive,
  SHARP_WAIT_EXTRA_MS,
  sharpWaitPollDelay,
} from "../functions/sharpWait";

// Flag REACT_APP_CLONE_INSTANT_SHARP_WAIT - build-time constant, so the
// export at the bottom is fixed for the lifetime of the bundle and hook
// order never varies at runtime (same idiom as useEditorUploadPendingPoll).
const sharpWaitEnabled = enviroment.cloneInstantSharpWaitEnabled;

type EditorSharpWaitPoll = (logo: BaseLogo | null | undefined) => void;

/**
 * Editor-level "Preparing sharp preview…" re-read (flag
 * REACT_APP_CLONE_INSTANT_SHARP_WAIT).
 *
 * While the open logo is in the sharp wait (upload-pending clone without a
 * genuine high-res, isSharpWaitActive) re-read ONLY its own GetLogo entry
 * (["logos", actAs, id]) on the 2/2/3/3/5/5/5/5 s schedule - max 8 calls,
 * 30 s, never a list call. The heal subscription (useEditorLogoHeal, flag
 * REACT_APP_LOGO_HEAL_PER_MUTATION) delivers the result: patchLogoFromDetail
 * swaps the high-res in (keepSharp rule) and syncs uploadPending / serial.
 * The loop stops the moment the logo leaves the wait - a genuine high-res
 * arrived, or the stamp cleared - or the tool moves to another logo (the
 * budget restarts per logo id).
 *
 * When uploadPending flips to false WITHOUT a high-res in that response,
 * ONE extra GetLogo follows after 1 s (the API stores the high-res right
 * before it clears the stamp); a high-res that lands before that second
 * cancels it. The 3/8/15 s pending re-read and the per-mutation heal both
 * stand down while this hook owns the logo (see their call sites), so the
 * budget is exactly this schedule - typically 2-3 calls per clone.
 */
function useEditorSharpWaitPollEnabled(
  logo: BaseLogo | null | undefined
): void {
  const queryClient = useQueryClient();
  const logoId = logo?.id ?? 0;
  const pending = isLogoUploadPending(logo);
  const sharp = hasGenuineHighRes(logo?.displayImage);
  const active = logoId > 0 && isSharpWaitActive(logo);
  const attempt = useRef(0);
  const [tick, setTick] = useState(0);

  const refetch = () => {
    void queryClient.invalidateQueries({
      queryKey: logoDetailKey(logoId),
      exact: true,
      refetchType: "all",
    });
  };
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  // The budget is per logo: another logo opened in the same tool starts
  // over; the same logo keeps its count across re-renders.
  useEffect(() => {
    attempt.current = 0;
  }, [logoId]);

  // The re-armed tick after every call makes the schedule independent of
  // the data identity (an API that still answers "pending, no high-res"
  // with structurally identical data would otherwise never re-run the
  // effect). Leaving the wait (active false) clears the pending timer.
  useEffect(() => {
    if (!active) return;
    const delay = sharpWaitPollDelay(attempt.current);
    if (delay === undefined) return;
    const timer = setTimeout(() => {
      attempt.current += 1;
      refetchRef.current();
      setTick((t) => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [active, logoId, tick]);

  // One extra call 1 s after the stamp cleared without a high-res. The
  // previous pending state is remembered per logo id so a tool opened on an
  // already-settled logo, or moved to another logo, never fires it. A
  // high-res that lands first re-runs the effect and its cleanup drops the
  // timer.
  const previous = useRef<{ logoId: number; pending: boolean }>({
    logoId: 0,
    pending: false,
  });
  useEffect(() => {
    const before = previous.current;
    previous.current = { logoId, pending };
    if (!logoId || before.logoId !== logoId) return;
    if (!before.pending || pending) return;
    if (sharp) return;
    const timer = setTimeout(() => refetchRef.current(), SHARP_WAIT_EXTRA_MS);
    return () => clearTimeout(timer);
  }, [logoId, pending, sharp]);
}

// Flag off: no hook body at all (no query-client read, no timer) - the call
// site keeps one hook call either way.
function useEditorSharpWaitPollDisabled(): void {}

export const useEditorSharpWaitPoll: EditorSharpWaitPoll = sharpWaitEnabled
  ? useEditorSharpWaitPollEnabled
  : useEditorSharpWaitPollDisabled;
